import { Router } from '@angular/router';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { PriceListData } from '../models';
import { PriceListArea } from '../models/pricelist-area.model';
import { PriceListRoutes } from '../models/pricelist.routes';
import { PriceListService } from '../services/pricelist.service';
import { SharedDataStore } from '../store';

export interface PriceListResolverDeps {
  priceListService: PriceListService;
  sharedDataStore: SharedDataStore;
  loadingService: LoadingService;
  router: Router;
}

/**
 * Mixin function to resolve price lists based on the provided dependencies.
 * It fetches price lists for a given area and year, handles loading states, and errors.
 */
export function withPriceListResolver<T extends PriceListResolverDeps>({ loadingService, priceListService, sharedDataStore, router }: T) {
  return {
    resolvePriceLists$: (priceListArea: PriceListArea, yearAndAccess$: Observable<{ year: string; privateAccess: boolean }>): Observable<PriceListData> => {
      return yearAndAccess$.pipe(
        tap(({ year }) => loadingService.startLoading(`Loading price lists for ${year}...`)),
        switchMap(({ year, privateAccess }) => priceListService.fetchAllData(priceListArea, Number(year), privateAccess)),
        catchError(() => {
          loadingService.stopLoading();
          router.navigate([PriceListRoutes.NOT_FOUND]).then();
          return of(undefined);
        }),
        tap(
          ({
            priceLists,
            juniorCampsPriceLists,
            youngAdultsPriceLists,
            ayaPriceLists,
            onlineCourses,
            publicHolidays,
            termsAndConditionsLanguages,
            termsAndConditionsKIL,
            termsAndConditionsALP,
          }) => {
            sharedDataStore.updateState({
              priceLists,
              juniorCampsPriceLists,
              youngAdultsPriceLists,
              ayaPriceLists,
              onlineCourses,
              publicHolidays,
              termsAndConditionsLanguages,
              priceListArea,
              termsAndConditionsKIL,
              termsAndConditionsALP,
            });
            loadingService.stopLoading();
          },
        ),
      );
    },
  };
}
